import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Super-admin-only diagnostic panel for the hermetic-lock enforcer.
 *
 * Fetches /api/admin/gates/<id>/hermetic-lock-debug on mount and
 * every 10 seconds while visible. Renders the exact snapshot the
 * enforcer would compute on its next tick — toggle, schedule,
 * window-membership, lock state, motion state, dedupe state, and
 * the resulting "next_action" decision with a Hebrew reason.
 *
 * Purpose: when a customer reports "auto-lock didn't fire at end-
 * of-day", look here first. The decision tree the enforcer walks
 * has six skip reasons; this panel surfaces which one is biting.
 *
 * Only renders for gates with wiring_type === 'toggle_with_stop'
 * — for other gates the hermetic-lock concept doesn't apply.
 */
function HermeticLockDebugPanel({ gateId, wiringType }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (wiringType !== 'toggle_with_stop') return undefined;
    let cancelled = false;

    const fetchState = () => {
      axios.get(`/api/admin/gates/${gateId}/hermetic-lock-debug`)
        .then(res => {
          if (cancelled) return;
          setState(res.data);
          setError(null);
        })
        .catch(err => {
          if (cancelled) return;
          setError(err.response?.data?.error || 'שגיאה בטעינה');
        });
    };

    fetchState();
    const interval = setInterval(fetchState, 10 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gateId, wiringType, refreshKey]);

  if (wiringType !== 'toggle_with_stop') return null;
  if (error) {
    return (
      <div className="info-card" style={{ marginBottom: '1.25rem', borderColor: '#fecaca', background: '#fef2f2' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>אבחון נעילה הרמטית</h3>
        <div style={{ color: '#991b1b', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</div>
      </div>
    );
  }
  if (state === null) return null;

  const actionStyle = {
    lock: { bg: '#dbeafe', fg: '#1e3a8a', label: '🔒 נעילה' },
    unlock: { bg: '#d1fae5', fg: '#065f46', label: '🔓 שחרור נעילה' },
    no_op: { bg: '#f3f4f6', fg: '#374151', label: '⏸ אין פעולה' },
  }[state.next_action] || { bg: '#f3f4f6', fg: '#374151', label: state.next_action };

  return (
    <div className="info-card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 600 }}>
          אבחון נעילה הרמטית
          <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: 400, marginInlineStart: '0.5rem' }}>
            (סופר אדמין · מתרענן כל 10 שניות)
          </span>
        </h3>
        <button
          type="button"
          onClick={() => setRefreshKey(k => k + 1)}
          style={{
            padding: '0.25rem 0.6rem',
            background: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: 5,
            fontSize: '0.78rem',
            cursor: 'pointer',
            fontFamily: 'Heebo, sans-serif',
          }}
        >
          ↻ רענן עכשיו
        </button>
      </div>

      {/* The headline: what would the enforcer do right now? */}
      <div style={{
        background: actionStyle.bg,
        color: actionStyle.fg,
        border: `1px solid ${actionStyle.fg}30`,
        borderRadius: 6,
        padding: '0.65rem 0.85rem',
        marginBottom: '0.75rem',
      }}>
        <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{actionStyle.label}</div>
        <div style={{ fontSize: '0.85rem', marginTop: '0.25rem', lineHeight: 1.5 }}>
          {state.next_action_reason}
        </div>
      </div>

      {/* The raw decision inputs, in a single readable list. */}
      <div style={{ fontSize: '0.83rem', lineHeight: 1.85 }}>
        <Row label="זמן ההערכה (ישראל)" value={formatIsraelTime(state.evaluated_at_israel)} />
        <Row
          label="נעילה הרמטית (הטוגל)"
          value={booleanWord(state.hermetic_lock_enabled)}
          highlight={!state.hermetic_lock_enabled ? 'red' : null}
        />
        <Row label="מקור ההגדרה" value={sourceLabel(state.settings_source)} />
        <Row
          label="חלון נעילה מוגדר"
          value={state.has_schedule ? 'כן' : 'לא'}
          highlight={!state.has_schedule ? 'red' : null}
        />
        {state.has_schedule && (
          <Row label="מקור הלוח" value={sourceLabel(state.schedule_source)} />
        )}
        {state.has_schedule && (
          <Row
            label="חלונות הנעילה"
            value={formatWindows(state.schedule_windows)}
            wide
          />
        )}
        <Row
          label="כרגע בתוך חלון נעילה?"
          value={booleanWord(state.in_lock_window)}
        />
        <Row
          label="חלון שחרור זמני פעיל?"
          value={booleanWord(state.in_temp_unlock)}
        />
        <Row
          label="צריך להיות נעול?"
          value={booleanWord(state.should_be_locked)}
        />
        <Row
          label="נעול כרגע (לפי audit)?"
          value={booleanWord(state.currently_locked)}
        />
        <Row label="השער פתוח?" value={booleanWord(state.gate_is_open)} />
        <Row label="השער בתנועה?" value={booleanWord(state.gate_in_motion)} />
        <Row
          label="חלון המתנה בין פקודות"
          value={state.movement_timeout_seconds
            ? `${state.movement_timeout_seconds} ש׳ (${movementTimeoutSourceShort(state.movement_timeout_source)})`
            : '—'}
        />
        <Row
          label="פקודת נעילה אחרונה ב-2 דקות?"
          value={booleanWord(state.has_recent_dedupe_lock)}
        />
        <Row
          label="פקודת שחרור אחרונה ב-2 דקות?"
          value={booleanWord(state.has_recent_dedupe_unlock)}
        />
      </div>
    </div>
  );
}


function Row({ label, value, highlight, wide }) {
  const highlightColor = highlight === 'red' ? '#dc2626' : null;
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '0.75rem',
      borderBottom: '1px dashed #f1f5f9',
      paddingBlock: '0.2rem',
    }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{
        fontWeight: 500,
        color: highlightColor || '#1f2937',
        textAlign: 'left',
        whiteSpace: wide ? 'normal' : 'nowrap',
        fontFamily: wide ? 'Heebo, sans-serif' : 'ui-monospace, SFMono-Regular, monospace',
      }}>
        {value}
      </span>
    </div>
  );
}

function booleanWord(v) {
  if (v === true) return 'כן';
  if (v === false) return 'לא';
  return '—';
}

function sourceLabel(s) {
  if (s === 'gate') return 'הגדרה ברמת השער';
  if (s === 'group') return 'הגדרה ברמת הקבוצה (ירושה)';
  if (s === 'default') return 'ברירת מחדל מערכת';
  return s || '—';
}

function movementTimeoutSourceShort(s) {
  // Compact version of the source label since it sits inline with
  // the seconds value. Long form lives on the gate-info card.
  if (s === 'auto') return 'מחושב';
  if (s === 'override') return 'עקיפה';
  if (s === 'fallback') return 'ברירת מחדל';
  return s || '—';
}

function formatIsraelTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('he-IL', {
    timeZone: 'Asia/Jerusalem',
    weekday: 'short',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

const _DAYS_HE = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

function formatWindows(windows) {
  if (!windows || windows.length === 0) return '—';
  return windows.map((w, i) => (
    <div key={i}>
      {_DAYS_HE[w.day_of_week] || `יום ${w.day_of_week}`}: {w.start_time} → {w.end_time}
    </div>
  ));
}

export default HermeticLockDebugPanel;
